import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX } from 'lucide-react';

const PORTRAIT_URL = "https://customer-assets-lxgj4vgw.emergentagent.net/job_142de766-7447-4fb5-b920-10398996bff4/artifacts/0pp8u9ck_fondo-cyborg-8k-vertical.webp";
const VIDEO_1_URL = "https://customer-assets-lxgj4vgw.emergentagent.net/job_142de766-7447-4fb5-b920-10398996bff4/artifacts/y035qfsr_grok-video-1493e2f2-2e2d-4fb7-bf8e-cb407aceb0a8.mp4";
const VIDEO_2_URL = "https://customer-assets-lxgj4vgw.emergentagent.net/job_142de766-7447-4fb5-b920-10398996bff4/artifacts/g8f7j2lg_grok-video-5c92eff4-e9d7-4ae8-ab6f-b02b5d0c2ff6.mp4";

const IntroSequence = ({ onComplete, onSkip }) => {
  const [stage, setStage] = useState('portrait'); // portrait, video1, video2, final
  const [isMuted, setIsMuted] = useState(false);
  const video1Ref = useRef(null);
  const video2Ref = useRef(null);

  useEffect(() => {
    // Start with portrait, then transition to video1 after 3 seconds
    const timer = setTimeout(() => {
      setStage('video1');
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (stage === 'video1' && video1Ref.current) {
      video1Ref.current.play().catch(err => {
        console.log('Autoplay blocked, trying muted:', err);
        setIsMuted(true);
        video1Ref.current.muted = true;
        video1Ref.current.play();
      });
    }
  }, [stage]);

  useEffect(() => {
    if (stage === 'video2' && video2Ref.current) {
      video2Ref.current.play().catch(err => {
        console.log('Autoplay blocked, trying muted:', err);
        setIsMuted(true);
        video2Ref.current.muted = true;
        video2Ref.current.play();
      });
    }
  }, [stage]);

  const handleVideo1End = () => {
    setStage('video2');
  };

  const handleVideo2End = () => {
    setStage('final');
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (video1Ref.current) video1Ref.current.muted = !isMuted;
    if (video2Ref.current) video2Ref.current.muted = !isMuted;
  };

  return (
    <div className="relative w-full h-screen overflow-hidden bg-[#0A0A0A]" data-testid="intro-sequence">
      {/* Skip Button */}
      <button
        onClick={onSkip}
        data-testid="skip-intro-button"
        className="absolute top-6 right-6 z-50 px-6 py-2 font-outfit text-sm tracking-wide text-white/80 hover:text-white backdrop-blur-xl bg-black/40 border border-neon-red/30 hover:border-neon-red hover:bg-black/60 transition-all duration-300"
      >
        SALTAR
      </button>

      {/* Mute/Unmute Button */}
      {(stage === 'video1' || stage === 'video2') && (
        <button
          onClick={toggleMute}
          data-testid="toggle-mute-button"
          className="absolute top-6 left-6 z-50 p-3 backdrop-blur-xl bg-black/40 border border-neon-red/30 hover:border-neon-red hover:bg-black/60 transition-all duration-300"
        >
          {isMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
        </button>
      )}

      <AnimatePresence mode="wait">
        {/* Stage 1: Portrait Photo */}
        {stage === 'portrait' && (
          <motion.div
            key="portrait"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2 }}
            className="absolute inset-0 w-full h-full"
            data-testid="intro-portrait"
          >
            <div className="relative w-full h-full">
              <img
                src={PORTRAIT_URL}
                alt="Alivan"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black opacity-60" />
              
              {/* Red glow effects */}
              <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-neon-red/20 blur-3xl rounded-full" />
              <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-neon-red/10 blur-3xl rounded-full" />
            </div>
          </motion.div>
        )}

        {/* Stage 2: Video 1 */}
        {stage === 'video1' && (
          <motion.div
            key="video1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="absolute inset-0 w-full h-full"
            data-testid="intro-video-1"
          >
            <video
              ref={video1Ref}
              src={VIDEO_1_URL}
              className="w-full h-full object-cover"
              muted={isMuted}
              playsInline
              onEnded={handleVideo1End}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/40" />
          </motion.div>
        )}

        {/* Stage 3: Video 2 */}
        {stage === 'video2' && (
          <motion.div
            key="video2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="absolute inset-0 w-full h-full"
            data-testid="intro-video-2"
          >
            <video
              ref={video2Ref}
              src={VIDEO_2_URL}
              className="w-full h-full object-cover"
              muted={isMuted}
              playsInline
              onEnded={handleVideo2End}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/40" />
          </motion.div>
        )}

        {/* Stage 4: Final Portrait with CTA */}
        {stage === 'final' && (
          <motion.div
            key="final"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            className="absolute inset-0 w-full h-full"
            data-testid="intro-final"
          >
            <div className="relative w-full h-full">
              <img
                src={PORTRAIT_URL}
                alt="Alivan"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/60" />
              
              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 1 }}
                className="absolute bottom-32 left-1/2 transform -translate-x-1/2 z-20"
              >
                <button
                  onClick={onComplete}
                  data-testid="continue-button"
                  className="px-12 py-4 font-outfit text-lg font-semibold tracking-wider text-white bg-black/60 backdrop-blur-xl border-2 border-neon-red hover:bg-neon-red hover:border-neon-red transition-all duration-300 shadow-[0_0_20px_rgba(255,0,51,0.5)] hover:shadow-[0_0_40px_rgba(255,0,51,0.8)]"
                >
                  HABLAR CON ALIVAN
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default IntroSequence;